// src/context/PreferencesContext.tsx
/**
 * Context for managing user preferences (chart colors, date formats, etc.)
 *
 * Provides effective preferences merged from platform defaults and account overrides.
 * Caches preferences to avoid repeated API calls.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getEffectivePreferences, type EffectivePreferences } from "@/api/preferences";
import { useAuth } from "@/context/AuthContext";

interface PreferencesContextValue {
  preferences: EffectivePreferences | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const defaultPreferences: EffectivePreferences = {
  chart_color_preset: "corporate",
  chart_colors: [],
  default_chart_type: "auto",
  date_format_preset: "short",
  date_order_preset: "dmy",
  source: "platform",
};

/** Exported as a test seam: a suite that needs a non-default preference
 *  provides the value directly instead of mounting the fetching provider. */
export const PreferencesContext = createContext<PreferencesContextValue>({
  preferences: defaultPreferences,
  loading: false,
  error: null,
  refresh: async () => {},
});

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState<EffectivePreferences | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = async () => {
    if (!isAuthenticated) {
      setPreferences(defaultPreferences);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const prefs = await getEffectivePreferences();
      setPreferences(prefs);
    } catch (e: any) {
      console.error("Failed to load preferences:", e);
      setError(e?.message || "Failed to load preferences");
      // Use defaults on error
      setPreferences(defaultPreferences);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, [isAuthenticated]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        error,
        refresh: fetchPreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  return useContext(PreferencesContext);
}

/**
 * Get just the date format preset, with fallback to "short"
 */
export function useDateFormat(): string {
  const { preferences } = usePreferences();
  return preferences?.date_format_preset || "short";
}

/**
 * Get just the date order preset, with fallback to "dmy"
 */
export function useDateOrder(): string {
  const { preferences } = usePreferences();
  return preferences?.date_order_preset || "dmy";
}
